import { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useCanvasStore } from '@/stores/canvasStore';
import { imageToOverlayPixels } from '@/utils/overlayTemplate';
import { showSnackbar } from '@/stores/snackbarStore';

const OPACITY_MIN = 0.2;
const OPACITY_MAX = 0.9;
const OPACITY_STEP = 0.1;

function clampCoord(v: number | string, max: number): number {
  const n = typeof v === 'string' ? parseInt(v, 10) : v;
  return Math.max(0, Math.min(max, Number.isFinite(n) ? Math.floor(n) : 0));
}

export function OverlayPanel() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const overlayPixels = useCanvasStore((state) => state.overlayPixels);
  const overlayBounds = useCanvasStore((state) => state.overlayBounds);
  const overlayVisible = useCanvasStore((state) => state.overlayVisible);
  const overlayOpacity = useCanvasStore((state) => state.overlayOpacity);
  const canvasWidth = useCanvasStore((state) => state.width);
  const canvasHeight = useCanvasStore((state) => state.height);
  const setOverlayPixels = useCanvasStore((state) => state.setOverlayPixels);
  const setOverlayVisible = useCanvasStore((state) => state.setOverlayVisible);
  const setOverlayOpacity = useCanvasStore((state) => state.setOverlayOpacity);
  const clearOverlay = useCanvasStore((state) => state.clearOverlay);

  const hasOverlay = overlayPixels !== null;
  const maxX = Math.max(0, canvasWidth - 1);
  const maxY = Math.max(0, canvasHeight - 1);

  const [regionTopLeftX, setRegionTopLeftX] = useState(0);
  const [regionTopLeftY, setRegionTopLeftY] = useState(0);
  const [regionBottomRightX, setRegionBottomRightX] = useState(
    () => useCanvasStore.getState().width - 1
  );
  const [regionBottomRightY, setRegionBottomRightY] = useState(
    () => useCanvasStore.getState().height - 1
  );

  const getBounds = () => {
    const x1 = clampCoord(regionTopLeftX, maxX);
    const y1 = clampCoord(regionTopLeftY, maxY);
    const x2 = clampCoord(regionBottomRightX, maxX);
    const y2 = clampCoord(regionBottomRightY, maxY);
    return {
      x1: Math.min(x1, x2),
      y1: Math.min(y1, y2),
      x2: Math.max(x1, x2),
      y2: Math.max(y1, y2),
    };
  };

  const handleLoadImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showSnackbar('Please select an image file', 'warning');
      e.target.value = '';
      return;
    }
    try {
      const bounds = hasOverlay && overlayBounds ? overlayBounds : getBounds();
      const { grid, bounds: clampedBounds } = await imageToOverlayPixels(file, {
        bounds,
        canvasWidth,
        canvasHeight,
      });
      setOverlayPixels(grid, clampedBounds);
      setOverlayVisible(true);
      showSnackbar('Template loaded. Adjust opacity to see the canvas underneath.', 'success');
    } catch (err) {
      showSnackbar(err instanceof Error ? err.message : 'Failed to load image', 'error');
    }
    e.target.value = '';
  };

  const handleClear = useCallback(() => {
    if (overlayBounds) {
      setRegionTopLeftX(overlayBounds.x1);
      setRegionTopLeftY(overlayBounds.y1);
      setRegionBottomRightX(overlayBounds.x2);
      setRegionBottomRightY(overlayBounds.y2);
    }
    clearOverlay();
  }, [overlayBounds, clearOverlay]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') setIsModalOpen(false);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isModalOpen, handleKeyDown]);

  const modalEl = isModalOpen ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => setIsModalOpen(false)}
        aria-hidden
      />
      <div
        className="relative bg-wall-surface border border-wall-border rounded-xl shadow-2xl w-full max-w-sm animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-wall-border/50">
          <h2 className="text-base font-semibold text-wall-fg">Overlay template</h2>
          <button
            type="button"
            onClick={() => setIsModalOpen(false)}
            className="p-1.5 rounded-lg text-wall-muted hover:text-wall-fg hover:bg-wall-border/50 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="px-4 py-4 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          {!hasOverlay ? (
            <>
              <div className="grid grid-cols-2 gap-x-2 gap-y-2 text-xs">
                <span className="col-span-2 text-wall-muted">
                  Region (canvas coords 0-{maxX}, 0-{maxY})
                </span>
                <label className="flex items-center gap-2">
                  <span className="text-wall-muted shrink-0 w-6">TL</span>
                  <input
                    type="number"
                    min={0}
                    max={maxX}
                    value={regionTopLeftX}
                    onChange={(e) => setRegionTopLeftX(clampCoord(e.target.value, maxX))}
                    className="flex-1 px-2 py-1.5 rounded-lg bg-wall-bg border border-wall-border text-wall-fg font-mono text-sm"
                    title="Top-left X"
                  />
                  <input
                    type="number"
                    min={0}
                    max={maxY}
                    value={regionTopLeftY}
                    onChange={(e) => setRegionTopLeftY(clampCoord(e.target.value, maxY))}
                    className="flex-1 px-2 py-1.5 rounded-lg bg-wall-bg border border-wall-border text-wall-fg font-mono text-sm"
                    title="Top-left Y"
                  />
                </label>
                <label className="flex items-center gap-2">
                  <span className="text-wall-muted shrink-0 w-6">BR</span>
                  <input
                    type="number"
                    min={0}
                    max={maxX}
                    value={regionBottomRightX}
                    onChange={(e) => setRegionBottomRightX(clampCoord(e.target.value, maxX))}
                    className="flex-1 px-2 py-1.5 rounded-lg bg-wall-bg border border-wall-border text-wall-fg font-mono text-sm"
                    title="Bottom-right X"
                  />
                  <input
                    type="number"
                    min={0}
                    max={maxY}
                    value={regionBottomRightY}
                    onChange={(e) => setRegionBottomRightY(clampCoord(e.target.value, maxY))}
                    className="flex-1 px-2 py-1.5 rounded-lg bg-wall-bg border border-wall-border text-wall-fg font-mono text-sm"
                    title="Bottom-right Y"
                  />
                </label>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLoadImage}
                aria-label="Load overlay template from image"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-10 flex items-center justify-center gap-2 bg-wall-accent/20 hover:bg-wall-accent/30 border border-wall-accent/40 rounded-lg transition-colors text-sm font-medium text-wall-fg"
                title="Load template from image"
              >
                Load image
              </button>
            </>
          ) : (
            <>
              {overlayBounds && (
                <p className="text-xs text-wall-muted">
                  Region ({overlayBounds.x1},{overlayBounds.y1}) → ({overlayBounds.x2},
                  {overlayBounds.y2})
                </p>
              )}
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={overlayVisible}
                  onChange={(e) => setOverlayVisible(e.target.checked)}
                  className="rounded border-wall-border bg-wall-bg text-wall-accent focus:ring-wall-accent w-4 h-4"
                />
                <span className="text-sm">Show overlay on canvas</span>
              </label>
              <div className="flex flex-col gap-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-wall-muted">Opacity</span>
                  <span className="text-sm font-mono text-wall-fg">
                    {Math.round(overlayOpacity * 100)}%
                  </span>
                </div>
                <input
                  type="range"
                  min={OPACITY_MIN}
                  max={OPACITY_MAX}
                  step={OPACITY_STEP}
                  value={overlayOpacity}
                  onChange={(e) => setOverlayOpacity(Number(e.target.value))}
                  className="w-full h-2.5 rounded-lg appearance-none bg-wall-border/50 accent-wall-accent"
                  title="Overlay opacity"
                />
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleLoadImage}
                aria-label="Replace overlay template"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-9 flex items-center justify-center bg-wall-border/50 hover:bg-wall-accent/80 rounded-lg transition-colors text-sm"
                title="Replace with another image"
              >
                Replace image
              </button>
              <button
                onClick={() => {
                  handleClear();
                  setIsModalOpen(false);
                }}
                className="w-full h-9 flex items-center justify-center border border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
                title="Clear overlay"
              >
                Clear overlay
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <button
        type="button"
        onClick={() => setIsModalOpen(true)}
        className="w-full flex items-center justify-center gap-2 bg-wall-surface/80 backdrop-blur-xs border border-wall-border/50 rounded-xl py-2.5 px-3 shadow-lg hover:bg-wall-border/30 transition-colors text-sm font-medium text-wall-fg"
        title="Overlay template"
      >
        {hasOverlay && overlayVisible ? (
          <span className="w-2 h-2 rounded-full bg-emerald-400 shrink-0" aria-hidden />
        ) : null}
        Template
      </button>
      {modalEl && createPortal(modalEl, document.body)}
    </>
  );
}
